import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { useFormik } from 'formik';
import { generalStore } from '../../../stores/GeneralStore';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { DefaultTextField } from '../../../components/mui-overrides/Textfield';
import { DefaultTextAreaField } from '../../../components/mui-overrides/Textarea';
import { postInvestedUsersNotification } from '../../../api/pmApi';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { UploadButton } from '../../../components/ui/UploadButton';
import Box from '@mui/material/Box';

interface Props {
  handleClose: () => void;
  open: boolean;
}

export const NotifyInvestorsDialog: FC<Props> = ({ handleClose, open }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { assetId } = useParams<{ assetId: string }>();

  const validationSchema = object({
    title: string().required(t('dashboard.assets.records.notifyInvestorsDialog.errors.noTitle')),
    text: string().required(t('dashboard.assets.records.notifyInvestorsDialog.errors.noText')),
    attachmentUrl: string().nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      title: '',
      text: '',
      attachmentUrl: undefined,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!assetId) return;
      console.log(values);
      setIsLoading(true);
      try {
        await postInvestedUsersNotification(assetId, values);
        handleCloseAndReset();
        generalStore.setInfoMsg(t('dashboard.assets.records.notifyInvestorsDialog.sent'));
      } catch (error) {
        generalStore.setError(t('dashboard.assets.forum.addvoting.error'), error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleCloseAndReset = () => {
    handleClose();
    formik.resetForm();
  };

  return (
    <Dialog open={open} onClose={handleCloseAndReset} fullWidth maxWidth="sm">
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{t('dashboard.assets.records.notifyInvestorsDialog.title')}</DialogTitle>
        <DialogContent>
          <p>{t('dashboard.assets.records.notifyInvestorsDialog.description')}</p>
          <DefaultTextField fieldName={'title'} label={t('dashboard.assets.records.addrecords.title.label')} formik={formik} />
          <DefaultTextAreaField fieldName="text" label={'Text'} formik={formik} />
          <div style={{ display: 'flex', alignItems: 'center', padding: '16px 0' }}>
            <Box sx={{ mr: 2 }}>
              <UploadButton
                disabled={false}
                fileUrl={formik.values.attachmentUrl || ''}
                setFileUrl={(fileUrl) => formik.setFieldValue('attachmentUrl', fileUrl || undefined)}
                text={t('dashboard.assets.records.notifyInvestorsDialog.upload')}
              />
            </Box>
            {formik.values.attachmentUrl ? (
              <Typography variant="button">{t('revenuePools.detail.form.props.document.label.uploaded')}</Typography>
            ) : (
              <Typography variant="button">{t('revenuePools.detail.form.props.document.label.notUploaded')}</Typography>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button type="button" color="inherit" onClick={handleCloseAndReset}>
            {t('dashboard.assets.forum.addvoting.cancel')}
          </Button>
          <LoadingButton loading={isLoading} type="submit" variant="contained" disableElevation color="primary" data-cy="add-voting-button">
            {t('dashboard.assets.records.notifyInvestorsDialog.send')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
